'use client'
import { motion, useReducedMotion } from 'framer-motion'
import type { ReactNode } from 'react'

const ease = [0.21, 0.47, 0.32, 0.98] as const

/**
 * Standard "fade up as it enters the viewport" wrapper. Respects
 * prefers-reduced-motion (initial=false → content is simply there, no fade —
 * same idiom as MarketReachClient/ContactCTA). `data-anim` marks the element
 * for the print/no-JS escape hatch in globals.css / layout <noscript>, which
 * overrides the SSR'd inline opacity:0 in contexts that never scroll or never
 * run JS.
 */
export default function FadeInUp({
  children,
  className,
  delay = 0,
  distance = 18,
  fromX = 0,
  once = true,
}: {
  children: ReactNode
  className?: string
  delay?: number
  distance?: number
  /** Horizontal entrance offset (px). Positive enters from the right. */
  fromX?: number
  once?: boolean
}) {
  const reduce = useReducedMotion()
  return (
    <motion.div
      data-anim
      initial={reduce ? false : { opacity: 0, y: distance, x: fromX }}
      whileInView={{ opacity: 1, y: 0, x: 0 }}
      viewport={{ once, amount: 0.2 }}
      transition={{ duration: 0.7, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  )
}
